import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { downloadCSV, getCSVString, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { Swapagg } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'
import { useMissingExternalMappings } from '@/react-query/queries/mappings'

// =====================
// INVALIDATE
// =====================

const useInvalidateMappings = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: ['mappings/mappings'],
    })
    queryClient.invalidateQueries({
      queryKey: ['mappings/missing-external-mappings'],
    })
  }
}

const useInvalidateSwapaggMappings = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: ['mappings/swapagg'] })
    queryClient.invalidateQueries({ queryKey: ['mappings/swapagg-control'] })
  }
}

// =====================
// MUTATIONS
// =====================

export const useAddMappingMutation = () => {
  const api = useApi()

  const invalidateMappings = useInvalidateMappings()

  return useMutation({
    mutationFn: (params: Swapagg.AddMappingBody) => api.swapagg.addMapping(params),
    onSuccess: () => {
      toast.success('Successfully added mapping to SWAPAGG')
      invalidateMappings()
    },
  })
}

export const useAddToSwapaggMutation = () => {
  const api = useApi()

  const invalidateSwapaggMappings = useInvalidateSwapaggMappings()

  return useMutation({
    mutationFn: (params: Swapagg.AddMappingToSwapaggBody) => api.swapagg.addSwapaggMapping(params),
    onSuccess: () => {
      toast.success('Added mapping to SWAPAGG')
      invalidateSwapaggMappings()
    },
  })
}

export const useDeleteMappingMutation = () => {
  const api = useApi()

  const invalidateMappings = useInvalidateMappings()

  return useMutation({
    mutationFn: (params: Swapagg.DeleteMappingParams) => {
      return api.swapagg.deleteMapping(params)
    },
    onSuccess: (_data, _variables) => {
      toast.success(`Deleted mapping for ${_variables.id}`)
      invalidateMappings()
    },
    onError: (error, _variables) => {
      toastApiError(error, `Error deleting mapping for ${_variables.id}`)
    },
  })
}

export const usePushMappingToSwapaggMutation = () => {
  const api = useApi()

  const invalidateSwapaggMappings = useInvalidateSwapaggMappings()

  return useMutation({
    mutationFn: (params: Swapagg.PushMappingToSwapaggParams) => {
      return api.swapagg.pushMappingToSwapagg(params)
    },
    onSuccess: (_data, params) => {
      toast.success(`Added entry ${params.id} with banktraderef ${params.id} to SWAPAGG`, {
        description: new Date().toISOString(),
      })
      invalidateSwapaggMappings()
    },
    onError: (error, _variables) => {
      toastApiError(error, `Error adding entry ${_variables.id} to SWAPAGG`)
    },
  })
}

export const useDeleteSwapaggMappingMutation = () => {
  const api = useApi()

  const invalidateSwapaggMappings = useInvalidateSwapaggMappings()

  return useMutation({
    mutationFn: (params: Swapagg.DeleteSwapaggMappingParams) => {
      return api.swapagg.deleteSwapaggMapping(params)
    },
    onSuccess: (_data, params) => {
      toast.success(`Removed entry ${params.id} from SWAPAGG`)
      invalidateSwapaggMappings()
    },
    onError: (error, params) => {
      toastApiError(error, `Error removing entry ${params.id} from SWAPAGG`)
    },
  })
}

export const useUpdateSwapaggMappingMutation = () => {
  const api = useApi()

  const invalidateSwapaggMappings = useInvalidateSwapaggMappings()

  return useMutation({
    mutationFn: (params: Swapagg.UpdateSwapaggMappingBody) => {
      return api.swapagg.updateSwapaggMapping(params)
    },
    onSuccess(_data, params) {
      toast.success(`Updated ${params.updatedField} from ${params.oldValue} to ${params.newValue}`)
      invalidateSwapaggMappings()
    },
    onError: (error, params) => {
      toastApiError(
        error,
        `Error updating ${params.updatedField} from ${params.oldValue} to ${params.newValue}`,
      )
    },
  })
}

export const useDownloadMappingsTemplateMutation = () => {
  const missingMappingsQuery = useMissingExternalMappings()
  const missingMappings = missingMappingsQuery.data

  return useMutation({
    mutationFn: async () => {
      if (!missingMappings?.length) {
        throw new Error('No missing mappings to download')
      }

      const csvMappings: Array<Swapagg.CSVMapping> = []

      missingMappings.forEach((mapping) => {
        const hasBank = !!mapping.Bank
        const hasCustodian = !!mapping.Custodian

        // 1... Add empty custodian
        if (!hasBank) {
          csvMappings.push({
            olddb_structurename: mapping.structurename,
            provider: '',
            is_agent_id: 'false',
            external_id: '',
            from_date: '',
          })
        }

        // 2... Add empty bank
        if (!hasCustodian) {
          csvMappings.push({
            olddb_structurename: mapping.structurename,
            provider: '',
            is_agent_id: 'true',
            external_id: '',
            from_date: '',
          })
        }
      })

      const csvString = getCSVString(csvMappings)
      downloadCSV(csvString, `missing-mappings-${toISO8601(new Date())}`)
    },
  })
}

export const useUploadMappingsMutation = () => {
  const api = useApi()

  const invalidateMappings = useInvalidateMappings()
  return useMutation({
    mutationFn: (params: Swapagg.UploadMappingsBody) => {
      return api.swapagg.uploadMappings(params)
    },
    onSuccess: (_data, _variables) => {
      toast.success('Mappings uploaded successfully')
    },
    onSettled(_data, _error, _variables) {
      invalidateMappings()
    },
  })
}
